import React, { useState, useMemo, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';

const containerStyle = {
  width: '100%',
  height: '500px'
};

const center = {
  lat: -10.9175, // Centralizado em Lagarto
  lng: -37.65
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function getColorByAtividade(atividade) {
  switch (atividade) {
    case 'Milho': return '#FBDE44'; // amarelo pastel
    case 'Soja': return '#e6ee9c'; // verde claro pastel
    case 'Pecuária (Gado de Corte)': return '#d7ccc8'; // marrom claro
    case 'Cana': return '#c8e6c9'; // verde escuro pastel
    case 'Pecuária (Gado de Leite)': return '#fffde7'; // laranja claro
    case 'Hortaliças': return '#dcedc8'; // verde
    case 'Algodão': return '#f3e5f5'; // lilás claro
    case 'Café': return '#bcaaa4'; // café claro
    case 'Eucalipto': return '#b2dfdb'; // teal claro
    case 'Citros': return '#fffde7'; // amarelo muito claro
    case 'Caprinos': return '#f5f5f5'; // cinza claro
    case 'Ovinos': return '#eeeeee'; // cinza mais claro
    case 'Piscicultura': return '#b3e5fc'; // azul claro
    case 'Apicultura': return '#fff8e1'; // dourado claro
    case 'Avicultura': return '#ffcdd2'; // vermelho claro
    case 'Suinocultura': return '#f8bbd0'; // rosa claro
    case 'Mandioca': return '#f5f5dc'; // bege
    case 'Banana': return '#fffde7'; // amarelo banana claro
    case 'Tomate': return '#ffcdd2'; // vermelho tomate claro
    case 'Uva': return '#ede7f6'; // roxo claro
    case 'Manga': return '#ffe0b2'; // laranja manga claro
    case 'Feijão': return '#f5f5f5'; // bege feijão claro
    case 'Arroz': return '#fafafa'; // quase branco
    case 'Pimentão': return '#c8e6c9'; // verde pimentão claro
    case 'Batata': return '#fff9c4'; // amarelo batata claro
    case 'Melancia': return '#ffcdd2'; // vermelho melancia claro
    case 'Abóbora': return '#ffe0b2'; // laranja abóbora claro
    case 'Coco': return '#f5f5f5'; // bege coco claro
    case 'Mamão': return '#ffe0b2'; // laranja mamão claro
    case 'Limão': return '#f0f4c3'; // verde limão claro
    default: return '#e0e0e0'; // cinza muito claro
  }
}

const Maps = ({ povoados = [] }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });
  const [map, setMap] = useState(null);
  const [localSelecionado, setLocalSelecionado] = useState(null);
  const [zoom, setZoom] = useState(11);
  const navigate = useNavigate();

  const handleVerRelatorio = (fazendaId) => {
    navigate(`/Relatorios?fazenda=${fazendaId}`);
  };

  const locaisValidos = useMemo(() =>
    povoados.filter(p =>
      p.latitude && p.longitude &&
      typeof p.latitude === 'number' && typeof p.longitude === 'number'
    ), [povoados]);

  const onLoad = useCallback(mapInstance => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(mapInstance => {
    setMap(null);
  }, []);

  if (!isLoaded) return <div>Carregando mapa...</div>;

  // Função utilitária para gerar PNG colorido dinamicamente
  function generateColoredMarker(color) {
    const size = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // Testa se a cor é válida para o canvas
    ctx.fillStyle = '#000';
    ctx.fillStyle = color;
    if (ctx.fillStyle === '#000000' && color.toLowerCase() !== '#000000') {
      // Se a cor não for reconhecida, usa fallback
      ctx.fillStyle = '#e0e0e0';
    }
    ctx.beginPath();
    ctx.arc(size/2, size/2, 12, 0, 2 * Math.PI, false);
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#bdbdbd';
    ctx.stroke();
    return canvas.toDataURL('image/png');
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onZoomChanged={() => {
        if (map) {
          setZoom(map.getZoom());
        }
      }}
    >
      {locaisValidos.map((povoado) => {
        // Cor fixa para povoados
        const color = '#388e3c';
        const iconObj = {
          url: generateColoredMarker(color),
          scaledSize: (window.google && window.google.maps) ? new window.google.maps.Size(32, 32) : undefined
        };
        return (
          <Marker
            key={povoado.id}
            position={{ lat: povoado.latitude, lng: povoado.longitude }}
            onClick={() => setLocalSelecionado(povoado)}
            icon={iconObj}
            title={`${povoado.nome} - Clique para detalhes`}
          />
        );
      })}

      {localSelecionado && (
        <InfoWindow
          position={{ lat: localSelecionado.latitude, lng: localSelecionado.longitude }}
          onCloseClick={() => setLocalSelecionado(null)}
        >
          <div style={{ minWidth: 200, padding: 12, borderRadius: 8, background: '#fff', boxShadow: '0 2px 8px #0001', border: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: 0, color: '#333', fontWeight: 600, fontSize: 16 }}>{localSelecionado.nome}</h3>
            {localSelecionado.regiao_id && (
              <p style={{ margin: '8px 0 4px', fontWeight: 500, fontSize: 14 }}>
                Região: <span style={{ color: '#333', fontWeight: 500 }}>{localSelecionado.regiao_id}</span>
              </p>
            )}
            <p style={{ margin: '4px 0', fontSize: 14 }}>Latitude: <b>{localSelecionado.latitude}</b></p>
            <p style={{ margin: '4px 0 8px', fontSize: 14 }}>Longitude: <b>{localSelecionado.longitude}</b></p>
            <button 
              onClick={() => navigate(`/Relatorios?povoado=${localSelecionado.id}`)}
              style={{
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                width: '100%',
                transition: 'background-color 0.2s ease',
                marginTop: '8px'
              }}
              onMouseOver={e => e.target.style.background = '#45a049'}
              onMouseOut={e => e.target.style.background = '#4CAF50'}
            >
              Ver Relatório do Povoado
            </button>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default Maps;
